import * as React from 'react';
import {Query, Mutation} from 'react-apollo';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    GetScheduledEmails as GetScheduledEmailsQuery,
    GetScheduledEmailsVariables as GetScheduledEmailsQueryVariables,
    QueuedEmail as QueuedEmailFragment,
    EditQueuedEmail as EditQueuedEmailMutation,
    EditQueuedEmailVariables as EditQueuedEmailMutationVariables,
} from 'nutshell-graphql-types';

import EDIT_QUEUED_EMAIL from '../graphql/mutations/edit-queued-email.graphql';
import GET_SCHEDULED_EMAILS from '../graphql/queries/get-scheduled-emails.graphql';

import {ScheduledEmailEditForm} from './scheduled-email-edit-form';

const LIMIT = 50;

type OwnProps = {
    onClose: () => void;
    scheduledEmailId: string;
};

type EmailById = {
    [key: string]: {
        id: string;
        subject: string;
        body: string;
    };
};

class ScheduledEmailQuery extends Query<
    GetScheduledEmailsQuery,
    GetScheduledEmailsQueryVariables
> {}

class EditMutation extends Mutation<EditQueuedEmailMutation, EditQueuedEmailMutationVariables> {}

export const ScheduledEmailEdit = (props: OwnProps) => {
    return (
        <EditMutation mutation={EDIT_QUEUED_EMAIL}>
            {(editScheduledEmail) => (
                <ScheduledEmailQuery
                    query={GET_SCHEDULED_EMAILS}
                    variables={{
                        limit: LIMIT,
                        filters: {messageId: props.scheduledEmailId},
                    }}
                >
                    {({data, loading, error}) => {
                        if (loading) {
                            return null;
                        }

                        const emails =
                            data && data.queuedEmails
                                ? data.queuedEmails.edges.map((emailNode) => {
                                      return emailNode.node;
                                  })
                                : [];

                        const initialValues = emailsToFormValues(emails);

                        return (
                            <ScheduledEmailEditForm
                                {...props}
                                isLoadingTemplates={loading}
                                // @ts-expect-error
                                templateListErrorMessage={safelyGetGraphQLErrorMessage(error)}
                                // @ts-expect-error
                                onSubmit={(values: EmailById) => {
                                    const payload = getChangedEmailsPayload(values, initialValues);

                                    if (payload) {
                                        return editScheduledEmail({
                                            variables: {payload: payload},
                                        });
                                    } else {
                                        return new Promise((resolve) => {
                                            // @ts-expect-error
                                            resolve();
                                        });
                                    }
                                }}
                                queuedEmails={emails}
                                editingEmailId={props.scheduledEmailId}
                                initialValues={initialValues}
                                closeModal={props.onClose}
                            />
                        );
                    }}
                </ScheduledEmailQuery>
            )}
        </EditMutation>
    );
};

const getChangedEmailsPayload = (formValues: EmailById, initialValues: EmailById) => {
    const changedEmails = [];

    for (const emailId in formValues) {
        if (Object.prototype.hasOwnProperty.call(formValues, emailId)) {
            const editedEmail = formValues[emailId];
            const initialEmail = initialValues[emailId];
            if (
                // @ts-expect-error
                editedEmail.subject !== initialEmail.subject ||
                // @ts-expect-error
                editedEmail.body !== initialEmail.body
            ) {
                changedEmails.push({
                    id: emailId,
                    // @ts-expect-error
                    subject: editedEmail.subject,
                    // @ts-expect-error
                    formattedBody: editedEmail.body,
                });
            }
        }
    }

    if (changedEmails.length) {
        return {
            queuedEmailsList: {
                queuedEmails: changedEmails,
            },
        };
    }

    return undefined;
};

const emailsToFormValues = (emails: QueuedEmailFragment[]): EmailById => {
    return emails.reduce<Record<string, any>>((accum, email) => {
        accum[email.id] = {
            body: email.bodyHtml,
            subject: email.subject,
        };

        return accum;
    }, {});
};
